import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import {
    clearPendingThrottle,
    createThrottleState,
    runThrottled,
    type ThrottledCallback,
} from '@apps/web/hooks/throttle_state';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';

function createScopedThrottleEntry(delay: number) {
    return {
        ...createThrottleState<Array<unknown>>(),
        delay,
        refCount: 0,
    };
}

type ScopedThrottleEntry = ReturnType<typeof createScopedThrottleEntry>;

const scopedThrottleEntries = new Map<string, ScopedThrottleEntry>();

function getOrCreateEntry(scope: string, delay: number): ScopedThrottleEntry {
    const existingEntry = scopedThrottleEntries.get(scope);
    if (existingEntry) {
        return existingEntry;
    }

    const newEntry: ScopedThrottleEntry = createScopedThrottleEntry(delay);
    scopedThrottleEntries.set(scope, newEntry);
    return newEntry;
}

/**
 * Creates a throttled callback whose timing state is shared by its scope.
 * The first instance's delay defines the shared window; later mismatches warn
 * in development and continue using that first delay. Each invocation keeps
 * its own callback, and `cancel()` resets this instance's local window and
 * clears its own pending shared call, leaving the shared window intact for
 * the other instances on the scope.
 */
export function useSharedThrottledCallback<TArgs extends Array<unknown>>(
    callback: (...args: TArgs) => unknown,
    delay: number,
    scope: string | null,
): ThrottledCallback<TArgs> {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    const local = useThrottledCallback(callback, delay);
    const scopedCallback = useCallback(
        (...args: Array<unknown>) => callbackRef.current(...(args as TArgs)),
        [],
    );

    useLayoutEffect(() => {
        if (!scope) {
            return;
        }

        const existingEntry = scopedThrottleEntries.get(scope);
        if (
            existingEntry &&
            process.env['NODE_ENV'] !== 'production' &&
            existingEntry.delay !== delay
        ) {
            // eslint-disable-next-line no-console
            console.warn(
                `Shared throttle scope "${scope}" has delays of ` +
                    `${existingEntry.delay}ms and ${delay}ms.`,
            );
        }

        const entry = getOrCreateEntry(scope, delay);
        entry.refCount += 1;

        return () => {
            const currentEntry = scopedThrottleEntries.get(scope);
            if (currentEntry !== entry) {
                return;
            }

            entry.refCount -= 1;
            if (entry.refCount > 0) {
                // A queued call belongs to the instance that queued it, so it
                // must not outlive that instance even though the shared entry
                // survives.
                if (entry.pendingCallback === scopedCallback) {
                    clearPendingThrottle(entry);
                }
                return;
            }

            clearPendingThrottle(entry);
            scopedThrottleEntries.delete(scope);
        };
    }, [delay, scope, scopedCallback]);

    const resolveEntry = useCallback((): ScopedThrottleEntry | undefined => {
        return scope ? scopedThrottleEntries.get(scope) : undefined;
    }, [scope]);

    const throttled = useCallback(
        (...args: TArgs): void => {
            const entry = resolveEntry();
            if (entry) {
                runThrottled(entry, entry.delay, scopedCallback, args);
            } else {
                local(...args);
            }
        },
        [local, resolveEntry, scopedCallback],
    );

    return useMemo(() => {
        const cancel = () => {
            local.cancel();
            const entry = resolveEntry();
            if (entry && entry.pendingCallback === scopedCallback) {
                clearPendingThrottle(entry);
            }
        };
        return Object.assign(
            (...args: TArgs) => {
                throttled(...args);
            },
            { cancel },
        );
    }, [local, resolveEntry, scopedCallback, throttled]);
}
